import { createAction } from 'redux-api-middleware'
import { action as azureBlockUploadAction } from 'abu-redux-middleware'

import { DateUtils, UrlUtils, FileUtils } from '../../utils'
import { Layers } from '../../constants'
import { HUBBLE_API_TOKEN } from '../../store/middleware/APIUrl'

const Types = {
    FETCH_LAYERS_REQUEST: 'FETCH_LAYERS##REQUEST',
    FETCH_LAYERS_SUCCESS: 'FETCH_LAYERS##SUCCESS',
    DOWNLOAD_LAYER_REQUEST: 'DOWNLOAD_LAYER##REQUEST',
    DOWNLOAD_LAYER_SUCCESS: 'DOWNLOAD_LAYER##SUCCESS',
    CREATE_LAYER_REQUEST: 'CREATE_LAYER#REQUEST',
    CREATE_LAYER_SUCCESS: 'CREATE_LAYER#SUCCESS',
    API_CALL_FAILURE: 'API_CALL##FAILURE',
    RETRY_UPLOAD_LAYER_REQUEST: 'RETRY_UPLOAD_LAYER##REQUEST',
    RETRY_UPLOAD_LAYER_SUCCESS: 'RETRY_UPLOAD_LAYER##SUCCESS',
    APPROVE_LAYER_REQUEST: 'APPROVE_LAYER##REQUEST',
    APPROVE_LAYER_SUCCESS: 'APPROVE_LAYER##SUCCESS',
    APPROVE_LAYER_FAILURE: 'APPROVE_LAYER##FAILURE',
    TEST_LAYER_REQUEST: 'TEST_LAYER##REQUEST',
    TEST_LAYER_SUCCESS: 'TEST_LAYER##SUCCESS',
    TEST_LAYER_FAILURE: 'TEST_LAYER##FAILURE',
    CHANGE_LAYERS: 'CHANGE_LAYERS',
    DELETE_LAYERS_REQUEST: 'DELETE_LAYERS##REQUEST',
    DELETE_LAYERS_SUCCESS: 'DELETE_LAYERS##SUCCESS',
}

/**
 * Fetch layers data
 */
function fetchLayers(page, { name } = {}) {
    const params = {}

    if (page > 0) {
        params.page = page
    }

    if (name) {
        params.name = name
    }

    const query = UrlUtils.buildQueryString(params)

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/layers?${query}`,
        method: 'GET',
        types: [Types.FETCH_LAYERS_REQUEST, { type: Types.FETCH_LAYERS_SUCCESS, meta: { noFirstPage: page > 1 } }, Types.API_CALL_FAILURE],
        options: {
            abortable: true,
        },
    })
}

/**
 * Create new layer item
 * @param {Object} layer
 * @param {Number} layer.date_end
 * @param {Number} layer.date_start
 * @param {String} layer.name
 * @param {String} layer.md5
 * @param {Number} layer.size
 * @param {String} layer.path
 * @param {String} layer.entry_point
 */
function createLayer(layer = {}) {
    const { md5, size, path, date_end, date_start, entry_point, name } = layer
    const body = JSON.stringify({
        offline: true,
        date_end: DateUtils.formatDB(date_end),
        date_start: DateUtils.formatDB(date_start),
        md5,
        path,
        entry_point,
        file_size: size,
        name,
    })

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/layers`,
        method: 'POST',
        types: [Types.CREATE_LAYER_REQUEST, Types.CREATE_LAYER_SUCCESS, Types.API_CALL_FAILURE],
        body,
    })
}

/**
 * Download a layer item
 * @param layerID The layer id
 */
function downloadLayer(layerID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/layers/${layerID}/download`,
        method: 'GET',
        types: [Types.DOWNLOAD_LAYER_REQUEST, Types.DOWNLOAD_LAYER_SUCCESS, Types.API_CALL_FAILURE],
    })
}

/**
 * Upload layer
 * @param {File} file
 * @param {Object} layer
 * @param {Number} layer.date_end
 * @param {Number} layer.date_start
 * @param {String} layer.name
 * @param {String} layer.md5
 * @param {Number} layer.size
 * @param {String} layer.entry_point
 * @param {Object} callbacks
 * @param {Function} callbacks.onSuccess
 * @param {Function} callbacks.onError
 * @param {Function} callbacks.onProgress
 */
function uploadLayer(file, layer, callbacks) {
    // Get name from file
    const { name: fileName } = file

    // Get new name for the file from layer
    const { name } = layer

    // Gets extension
    const extension = FileUtils.getFileExtension(fileName)

    // Compose new file name
    const uploadName = name ? `${name}.${extension}` : fileName

    return async (dispatch) => {
        // Gets md5 hash of the file
        const md5 = FileUtils.checksum(await FileUtils.readFile(file))

        // Creates the entry in the db
        const { type, payload: layerecord } = await dispatch(
            createLayer({
                ...layer,
                path: uploadName,
                md5,
            })
        )

        if (type !== Types.CREATE_LAYER_SUCCESS) {
            throw new Error('Error when creating layer')
        }

        const { id, upload_url: urlToUpload } = layerecord || {}

        if (!id) {
            throw new Error('Cannot find layer ID')
        }

        await dispatch(azureBlockUploadAction(urlToUpload, file, callbacks))

        return layerecord
    }
}

/**
 * Tries to upload a file again
 * @param {Number} layerID
 * @deprecated
 */
function retryUploadLayer(layerID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/layers/${layerID}/retry_upload`,
        method: 'POST',
        types: [Types.RETRY_UPLOAD_LAYER_REQUEST, Types.RETRY_UPLOAD_LAYER_SUCCESS, Types.API_CALL_FAILURE],
    })
}

/**
 * Approve layer
 * @param {Number} layerID
 * @param {Boolean} [inSelectedPlaylist]
 */
function approveLayer(layerID, isApproved, inSelectedPlaylist = false) {
    const body = JSON.stringify({
        is_approved: isApproved ? Layers.approvalStatus.approved : Layers.approvalStatus.rejected,
    })

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/layers/${layerID}/approve`,
        method: 'POST',
        body,
        types: [
            {
                type: Types.APPROVE_LAYER_REQUEST,
                meta: { layerID, inSelectedPlaylist },
            },
            {
                type: Types.APPROVE_LAYER_SUCCESS,
                meta: { layerID, inSelectedPlaylist },
            },
            {
                type: Types.APPROVE_LAYER_FAILURE,
                meta: { layerID, inSelectedPlaylist },
            },
        ],
    })
}

/**
 * Sets layer as tested
 * @param {Number} layerID
 * @param {Boolean} isOk true means is working fine, false it doesn't work
 * @param {Boolean} [inSelectedPlaylist]
 */
function testLayer(layerID, isOk, inSelectedPlaylist = false) {
    const body = JSON.stringify({
        is_tested: isOk ? Layers.testingStatus.approved : Layers.testingStatus.rejected,
    })

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/layers/${layerID}/test`,
        method: 'POST',
        body,
        types: [
            {
                type: Types.TEST_LAYER_REQUEST,
                meta: { layerID, inSelectedPlaylist },
            },
            {
                type: Types.TEST_LAYER_SUCCESS,
                meta: { layerID, inSelectedPlaylist },
            },
            {
                type: Types.TEST_LAYER_FAILURE,
                meta: { layerID, inSelectedPlaylist },
            },
        ],
    })
}

const changeLayersArray = (layers) => ({
    type: Types.CHANGE_LAYERS,
    payload: layers,
})

function deleteLayer(layerID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/layers/${layerID}`,
        method: 'DELETE',
        types: [Types.DELETE_LAYERS_REQUEST, Types.DELETE_LAYERS_SUCCESS, Types.API_CALL_FAILURE],
    })
}

export { Types, approveLayer, createLayer, downloadLayer, fetchLayers, retryUploadLayer, testLayer, uploadLayer, changeLayersArray, deleteLayer }
